import React, { useState, useContext, useEffect } from 'react';
import {
    Grid,
    Table,
    TableHeaderRow,
    TableFilterRow,
    TableColumnVisibility,
    PagingPanel,
} from '@devexpress/dx-react-grid-bootstrap4';

import { SelectionState, FilteringState, PagingState, CustomPaging, SortingState } from '@devexpress/dx-react-grid';
import { ChangeTablePage, SortTable } from '../tables/shared/TableEventHelpers';
import { DataContext } from '../../stores/course-table-store';
import { GetCourses } from './requests/CourseRequests';
import ExlCellComponent from './ExlCellComponent';
import Loading from '../Loading';
import TableRow from '../tables/shared/TableRow';
import TableHeader from '../tables/shared/TableHeader';
import ColumnChooser from '../tables/shared/ColumnChooser';
import TableFooter from '../tables/shared/TableFooter';
import FilterResetter from '../tables/shared/FilterResetter';
import NumberOfSearchResults from '../tables/shared/NumberOfSearchResults';
import NoDataRow from '../tables/shared/NoDataRow';
import ConvertTableToExcel from '../tables/shared/ConvertTableToExcel';
import FilterCell from './CourseFilterCell';
import { DataContext as CovDataStore } from '../../store';
import moment from 'moment';
import { CourseFilterOperations } from '../tables/shared/FilterOperations';
import { PagingShowAllText } from '../../functions/TableFunctions';
import { courseCategoryOptions, courseLanguageOptions, courseStatusOptions, translateOptionValue, bookingStatusOptions, courseFormatOptions } from './shared/translations';
import { hideColumsDependingOnWindowSize } from './CourseTableHelperFunctions';
import { useTranslation } from 'react-i18next';
import { COURSE_TABLE_NO_END_DATE, COURSE_TABLE_NO_START_DATE, COURSE_TABLE_RESERVE_SPOTS_OPEN, NO, YES } from '../../i18n/keyConstants';

const CourseTable = () => {
    const { store: covStore } = useContext(CovDataStore);
    const user = covStore?.user;
    const { store, dispatch } = useContext(DataContext);
    const [isLoading, setIsLoading] = useState(false);
    const error = null;
    const {
        columns,
        defaultFilters,
        hiddenColumns,
        pageSize,
        currentPage,
        tableSize,
        filters,
        tableColumnExtensions,
        courses,
        direction,
        sortInfo,
        columnName,
    } = store;

    const { t } = useTranslation(['courseTable', 'shared']);

    function removeColumnOnUserPermission() {
        if (!user.isExlAdmin) {
            var filteredColumns = columns.filter((col) => col.name !== 'category');
            dispatch({
                type: 'SET_COLUMNS',
                columns: filteredColumns,
            });
        }
    }

    function translateColumnTitles() {
        columns.map((col) => {
            col.title = t(col.title);
        })
    }

    useEffect(() => {
        window.addEventListener('resize', (() => hideColumsDependingOnWindowSize(dispatch)));
        hideColumsDependingOnWindowSize(dispatch);

        return () => {
            window.removeEventListener('resize', (() => hideColumsDependingOnWindowSize(dispatch)));
        }
    }, []);

    async function getCourses() {
        setIsLoading(true);
        const { rows, totalRows } = await GetCourses(pageSize, currentPage, direction, columnName, JSON.stringify(filters));
        handleRowValueFormatting(rows);
        dispatch({
            type: 'FETCH_SUCCESS',
            courses: rows,
            totalRows: totalRows,
        });
        setIsLoading(false);
        PagingShowAllText(t);
    }

    const handlePageSizeChange = (pageSize) => {
        ChangeTablePage(pageSize, dispatch, currentPage, tableSize);
    };

    const handleSortChange = (sorting) => {
        let shouldSort = true;

        if (shouldSort) {
            SortTable(sorting, sortInfo, dispatch);
        }
    };

    const handleRowValueFormatting = (allCourses) => {
        allCourses.forEach(function (course) {
            course.teacherName = course.teachers;
            course.startDate = moment.utc(course.startDate).local().isValid() ? moment.utc(course.startDate).local().format('YYYY-MM-DD') : t(COURSE_TABLE_NO_START_DATE);
            course.endDate = moment.utc(course.endDate).local().isValid() ? moment.utc(course.endDate).local().format('YYYY-MM-DD') : t(COURSE_TABLE_NO_END_DATE);
            course.status = t(translateOptionValue(course.status, courseStatusOptions), {ns: 'shared'});
            course.category = t(translateOptionValue(course.category, courseCategoryOptions), { ns: 'shared' });
            course.language = t(translateOptionValue(course.language, courseLanguageOptions), { ns: 'shared' });
            course.isRegistered = course.isRegistered ? t(YES, { ns: 'shared' }) : t(NO, { ns: 'shared' });
            course.format = t(translateOptionValue(course.format, courseFormatOptions), { ns: 'shared' });

            const fullCourse = course.numBookings >= course.maxNumBookings;
            const fullReserve = course.numReserves >= course.maxNumReserves;

            if (user.isExlAdmin) {
                course.numBookings = course.numBookings + ' / ' + course.maxNumBookings;
            } else {
                course.numBookings = (fullCourse && !fullReserve)  ? t(COURSE_TABLE_RESERVE_SPOTS_OPEN) : fullCourse ? "Full" : course.numBookings + ' / ' + course.maxNumBookings;
            }
        });
    }

    useEffect(() => {
        getCourses();
        removeColumnOnUserPermission();
        translateColumnTitles();
    }, [currentPage, filters, pageSize, direction, columnName]);

    return (
        <Grid columns={columns} rows={courses} className='padding-right-60px' >
            <PagingState
                pageSize={pageSize}
                currentPage={currentPage - 1}
                onPageSizeChange={handlePageSizeChange}
                onCurrentPageChange={(currentPage) => {
                    dispatch({ type: 'CURRENT_PAGE', payload: currentPage + 1 });
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }}
            />
            <SortingState onSortingChange={handleSortChange} defaultSorting={[{ columnName, direction }]} />
            <CustomPaging totalCount={tableSize} />
            <SelectionState />
            <FilteringState defaultFilters={defaultFilters} />
            <Table
                columnExtensions={tableColumnExtensions}
                cellComponent={ExlCellComponent}
                rowComponent={(props) => TableRow(props, error, 'course')}
                noDataRowComponent={(props) => NoDataRow(isLoading, error, props)}/>
            <CourseFilterOperations />
            <TableFilterRow  cellComponent={FilterCell} />
            <TableHeaderRow showSortingControls />
            <PagingPanel pageSizes={[10, 25, 50, 10000]} messages={PagingShowAllText} />
            <TableColumnVisibility hiddenColumnNames={hiddenColumns} />
            <TableHeader tableSize={tableSize}>
                <ConvertTableToExcel filters={filters} columns={columns} hiddenColumns={hiddenColumns} table='exlCourseFront' />
                <ColumnChooser
                    hiddenColumns={hiddenColumns}
                    columns={columns}
                    onHiddenColumnChange={(hiddenColumns) => {
                        dispatch({ type: 'HIDDEN_COLUMNS_AND_CUSTOM_COLS', hiddenColumns, showCustomCols: true });
                    }}
                    onReset={() => {
                        dispatch({ type: 'RESET_HIDDEN_COLUMNS' });
                    }}
                />
                <FilterResetter DataContext={DataContext} />
            </TableHeader>
            {isLoading ? <Loading /> : null}
            <TableFooter>
                <NumberOfSearchResults pageSize={pageSize} tableSize={tableSize} currentPage={currentPage} t={t} />
            </TableFooter>
        </Grid>
    );

};

export default CourseTable;